import { ServerToClientMessage } from '@all-for-one/shared-types'
import type { Question } from '@all-for-one/shared-types'
import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { createWsClient, WsClient, ConnectionState } from 'util/ws-client'

export type StudentLiveStatus = 'connecting' | 'waiting' | 'questions' | 'interrupt' | 'done'

export interface StudentLiveConnection {
	status: StudentLiveStatus
	setStatus: React.Dispatch<React.SetStateAction<StudentLiveStatus>>
	questions: Question[]
	currentIndex: number
	setCurrentIndex: React.Dispatch<React.SetStateAction<number>>
	learningStage: string | null
	classId: string
	lessonId: string
	lessonName: string
	connectionState: ConnectionState
	sessionEndedTick: number
}

/**
 * Owns the WebSocket lifecycle, server-message dispatch, the
 * connecting/waiting/questions/interrupt/done status state machine, and
 * reconnect save/restore. `status` is also driven externally (the answer
 * -submission flow sets it to 'done'), so `setStatus` is part of this
 * hook's public contract, not just its internal state.
 */
export const useStudentLiveConnection = (): StudentLiveConnection => {
	const { sessionCode } = useParams()
	const navigate = useNavigate()

	const [ status, setStatus ] = useState<StudentLiveStatus>('connecting')
	const [ questions, setQuestions ] = useState<Question[]>([])
	const [ currentIndex, setCurrentIndex ] = useState(0)
	const [ learningStage, setLearningStage ] = useState<string | null>(null)
	const [ classId, setClassId ] = useState<string>('')
	const [ lessonId, setLessonId ] = useState<string>('')
	const [ lessonName, setLessonName ] = useState<string>('')
	const [ sessionEndedTick, setSessionEndedTick ] = useState(0)
	const [ connectionState, setConnectionState ] = useState<ConnectionState>({ status: 'connecting' })
	const [ savedStatus, setSavedStatus ] = useState<StudentLiveStatus>('connecting')

	const StudentMessageListener = (msg: ServerToClientMessage) => {
		// Type only — payloads carry question content and student ids, and this
		// logs to the console of shared classroom devices
		console.log('StudentMessageListener', msg.type)
		switch(msg.type) {
			case 'system':
				if (msg.event === 'authenticated') {
					console.log('authenticated')
					if (!sessionCode) {
						console.error('[BUG] sessionCode is undefined! Redirecting to enter-code page.')
						navigate('/enter-code')
						return
					}
					wsClient.send({ type: 'session:join', code: sessionCode })
				}
				break
			case 'session:invalid':
				console.log('Session invalid - lesson has ended or code expired')
				navigate('/enter-code', { state: { error: 'That code is invalid or has expired. Please ask your teacher for a new code.' } })
				break
			case 'session:joined':
				console.log('session:joined')
				setStatus('waiting')
				setLessonId(msg.lessonId)
				setClassId(msg.classId)
				if (msg.lessonName) setLessonName(msg.lessonName)
				break
			case 'question:deliver':
				console.log('question:deliver')
				setQuestions(msg.payload)
				setLearningStage(msg.learningStage)
				setCurrentIndex(0)
				setStatus('questions')
				break
			case 'question:end':
				setQuestions([])
				setCurrentIndex(0)
				setLearningStage(null)
				setStatus('interrupt')
				setTimeout(() => {
					// Use callback form to get current status value (not captured value from closure)
					setStatus(currentStatus => {
						// Only switch to waiting if still on interrupt screen
						if (currentStatus === 'interrupt') {
							return 'waiting'
						}
						return currentStatus
					})
				}, 3000)
				break
			case 'session:ended':
				setSessionEndedTick(t => t + 1)
				break
		}
	}

	const wsClientRef = useRef<WsClient>(createWsClient())
	const wsClient = wsClientRef.current

	useEffect(() => {
		wsClient.onMessage(StudentMessageListener)
		wsClient.connect()

		return () => {
			wsClient.offMessage(StudentMessageListener)
			wsClient.close()
		}
	}, [sessionCode])

	// Listen for connection state changes
	useEffect(() => {
		const handleConnectionStateChange = (state: ConnectionState) => {
			setConnectionState(state)

			// When reconnecting or disconnected, show connecting screen
			if (state.status === 'reconnecting' || state.status === 'connecting') {
				// Save current status so we can restore it after reconnect
				if (status !== 'connecting') {
					setSavedStatus(status)
				}
				setStatus('connecting')
			}

			// When connected, restore previous status
			if (state.status === 'connected' && savedStatus !== 'connecting') {
				// Don't restore 'interrupt' status - it's a temporary 3-second screen
				// If we saved 'interrupt', switch to 'waiting' instead
				setStatus(savedStatus === 'interrupt' ? 'waiting' : savedStatus)
			}
		}

		wsClient.onConnectionStateChange(handleConnectionStateChange)

		return () => {
			wsClient.offConnectionStateChange(handleConnectionStateChange)
		}
	}, [status, savedStatus])

	return {
		status,
		setStatus,
		questions,
		currentIndex,
		setCurrentIndex,
		learningStage,
		classId,
		lessonId,
		lessonName,
		connectionState,
		sessionEndedTick,
	}
}
